import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

import { useDeleteDraftContext, usePreferredOptionContext, useSaveDraftContext } from './PricingToolSessionContext';
import { PRICING_OPTION_LABELS } from './constants';
import CopyFromPreviousOpportunityDialog from './copyFromPrevious/CopyFromPreviousOpportunityDialog';
import ExcelImportDialog from './excelImport/ExcelImportDialog';
import { exportCommercialApprovalHtml } from './export/exportCommercialApprovalHtml';
import { exportCommercialApprovalPdf } from './export/exportCommercialApprovalPdf';
import { useCommercialApprovalData } from './export/useCommercialApprovalData';
import SubmitToSalesforceButton from './submit/SubmitToSalesforceButton';
import type { PricingOptionIndex } from './types';

type PricingToolToolbarActionsProps = {
  offerId?: string;
};

export default function PricingToolToolbarActions({ offerId }: PricingToolToolbarActionsProps) {
  const { preferredOption, setPreferredOption } = usePreferredOptionContext();
  const { saveDraft, isSavingDraft } = useSaveDraftContext();
  const { deleteDraft, isDeletingDraft, hasDraft } = useDeleteDraftContext();
  const commercialApprovalData = useCommercialApprovalData();
  const { enqueueSnackbar } = useSnackbar();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isExcelImportOpen, setIsExcelImportOpen] = useState(false);

  const handlePreferredOptionChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setPreferredOption(Number(value) as PricingOptionIndex);
  };

  const handleSaveSession = async () => {
    try {
      await saveDraft();
      enqueueSnackbar('Pricing tool session saved', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to save pricing tool session: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  const handleDeleteSession = async () => {
    try {
      await deleteDraft();
      setDeleteDialogOpen(false);
      enqueueSnackbar('Pricing tool session deleted', { variant: 'success' });
    } catch (error) {
      setDeleteDialogOpen(false);
      enqueueSnackbar(`Failed to delete pricing tool session: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  const requireCommercialApprovalData = () => {
    if (!commercialApprovalData) {
      enqueueSnackbar('Add package pricing to the preferred option before exporting.', { variant: 'warning' });
      return null;
    }
    return commercialApprovalData;
  };

  const handleExportHtml = () => {
    setExportMenuAnchor(null);
    const data = requireCommercialApprovalData();
    if (data) {
      exportCommercialApprovalHtml(data);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuAnchor(null);
    const data = requireCommercialApprovalData();
    if (!data) {
      return;
    }
    setIsExportingPdf(true);
    try {
      await exportCommercialApprovalPdf(data);
    } catch {
      enqueueSnackbar('Failed to generate the Commercial Approval PDF.', { variant: 'error' });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportAll = () => {
    setExportMenuAnchor(null);
    window.print();
  };

  return (
    <Stack alignItems="center" direction="row" justifyContent="flex-end" spacing={1} useFlexGap>
      <Button disabled={!offerId} onClick={() => setIsExcelImportOpen(true)} sx={{ mr: 'auto' }} variant="contained">
        Upload Excel
      </Button>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="preferred-option-label">Preferred option</InputLabel>
        <Select
          label="Preferred option"
          labelId="preferred-option-label"
          onChange={handlePreferredOptionChange}
          value={String(preferredOption)}
        >
          {PRICING_OPTION_LABELS.map((_, index) => (
            <MenuItem key={index} value={String(index)}>
              {PRICING_OPTION_LABELS[index]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        color="error"
        disabled={!hasDraft || isDeletingDraft}
        onClick={() => setDeleteDialogOpen(true)}
        size="small"
        variant="outlined"
      >
        Delete Session
      </Button>
      <Button disabled={!offerId} onClick={() => setIsCopyDialogOpen(true)} size="small" variant="outlined">
        Copy from previous
      </Button>
      <Button disabled={isSavingDraft} onClick={handleSaveSession} size="small" variant="outlined">
        {isSavingDraft ? 'Saving...' : 'Save Session'}
      </Button>
      <Button
        disabled={isExportingPdf}
        onClick={(event) => setExportMenuAnchor(event.currentTarget)}
        size="small"
        variant="outlined"
      >
        Export
      </Button>
      <SubmitToSalesforceButton forecast={commercialApprovalData?.forecastSummary ?? null} offerId={offerId} />
      <Menu anchorEl={exportMenuAnchor} onClose={() => setExportMenuAnchor(null)} open={Boolean(exportMenuAnchor)}>
        <MenuItem onClick={handleExportPdf}>
          <ListItemText primary="Commercial Approval Export (PDF)" />
        </MenuItem>
        <MenuItem onClick={handleExportHtml}>
          <ListItemText primary="Commercial Approval HTML" />
        </MenuItem>
        <MenuItem onClick={handleExportAll}>
          <ListItemText primary="Export All" />
        </MenuItem>
      </Menu>

      <Dialog onClose={() => setDeleteDialogOpen(false)} open={deleteDialogOpen}>
        <DialogTitle>Delete pricing tool session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the saved draft for this offer. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" disabled={isDeletingDraft} onClick={handleDeleteSession} variant="contained">
            {isDeletingDraft ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {offerId && (
        <ExcelImportDialog offerId={offerId} onClose={() => setIsExcelImportOpen(false)} open={isExcelImportOpen} />
      )}

      {offerId && (
        <CopyFromPreviousOpportunityDialog
          offerId={offerId}
          onClose={() => setIsCopyDialogOpen(false)}
          open={isCopyDialogOpen}
        />
      )}
    </Stack>
  );
}
